import React, { useState } from "react";
import { Box, Button, Link } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import colors from "../../../../common/styles/colors";
import { IPersonBadge, Nullable } from "../../../../common/types";
import { MshOrganisation, useGetCompanyBadges } from "../table/hooks/useGetCompanyBadges";
import { CompanySharersDialog } from "../table/components/companySharersDialog/CompanySharersDialog";
import { Routes } from "../../../../routes";
import { CompanySearchResult } from "../../../search/types";
import { useSelectedCompanyData } from "../table/hooks/useSelectedCompanyData";
import { useExhibitorDetailsData } from "../table/hooks/useExhibitorDetailsData";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  title: {
    fontSize: "22px",
    fontWeight: 600,
    margin: 0,
    marginTop: "10px"
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginTop: "8px"
  },
  stands: {
    fontSize: "16px",
    fontWeight: 400,
    color: colors.darkGrey1,
    margin: 0
  },
  separator: {
    margin: "0 8px",
    color: colors.darkGrey1
  },
  sharerButton: {
    padding: 0,
    minWidth: "auto",
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 600,
    color: colors.primary
  },
  mshLink: {
    fontSize: 15,
    fontWeight: 500,
    textDecoration: "none",
    cursor: "pointer"
  },
  prefix: {
    fontSize: 16,
    fontWeight: 400,
    color: colors.darkGrey1
  },
  logo: {
    height: 85
  },
  backIcon: {
    fontSize: "65px"
  }
});

type Props = {
  companyData: IPersonBadge["organisation"];
  leadManagerCode?: Nullable<string>;
  mshOrganisation?: Nullable<MshOrganisation>;
  selectedCompany?: CompanySearchResult;
  shouldShowExhibitorDetails?: boolean;
};

export const CompanyPageHeader = ({
  companyData,
  leadManagerCode,
  mshOrganisation,
  selectedCompany,
  shouldShowExhibitorDetails
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation("common");

  const { getCompanyLogo } = useGetCompanyBadges({ selectedCompany });
  const logo = getCompanyLogo(companyData);
  const [isSharersDialogOpen, setSharersDialogOpen] = useState(false);

  const stands = companyData.standIds.join(", ");

  const handleOpenSharers = () => setSharersDialogOpen(true);
  const handleCloseSharers = () => setSharersDialogOpen(false);

  const companyName = companyData.displayName;
  const isSharerPage = Boolean(mshOrganisation);
  const hasStands = stands.length > 0;
  const showStands = hasStands && shouldShowExhibitorDetails;
  const showLeadManagerCode = leadManagerCode !== undefined && shouldShowExhibitorDetails;

  return (
    <>
      <Box className={classes.container}>
        <Box>
          <p className={classes.title}>{companyName}</p>

          {shouldShowExhibitorDetails && (
            <Box className={classes.infoRow}>
              {showStands && (
                <>
                  <p className={classes.stands} data-testid="company-page-stands">
                    {t("companyPage.stand")} {stands}
                  </p>
                  <span className={classes.separator}>|</span>
                </>
              )}
              {isSharerPage && mshOrganisation ? (
                <Link
                  component={RouterLink}
                  to={Routes.company}
                  state={{ company: { organisation: mshOrganisation } }}
                  className={classes.mshLink}
                  data-testid="msh-company-link"
                >
                  <span className={classes.prefix}>{t("companyPage.sharersDialog.mshPrefix")} </span>
                  {mshOrganisation.displayName}
                </Link>
              ) : (
                <Button variant="text" className={classes.sharerButton} onClick={handleOpenSharers}>
                  {t("companyPage.sharersDialog.linkLabel")}
                </Button>
              )}

              {showLeadManagerCode && (
                <>
                  <span className={classes.separator}>|</span>
                  <p className={classes.stands}>
                    {t("companyPage.leadManagerCode")} {leadManagerCode || ""}
                  </p>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box>
          {logo ? (
            <img src={logo} alt="company-logo" className={classes.logo} />
          ) : (
            <CorporateFareIcon className={classes.backIcon} color="primary" />
          )}
        </Box>
      </Box>

      <CompanySharersDialog
        open={isSharersDialogOpen}
        onClose={handleCloseSharers}
        participatingOrganisationId={companyData.participatingOrganisationId}
        companyName={companyName}
        mshOrganisation={companyData}
      />
    </>
  );
};
